import { useEffect, useRef } from "react"
import { groupBy } from "es-toolkit/array"
import { GraphQLSubscriptionConfig, requestSubscription } from "relay-runtime"
import { chMedicalReportSubscription as useChMedicalReportSubscriptionType } from "@/modules/patients/services/ch/__generated__/chMedicalReportSubscription.graphql"
import { chMedicalReportSubscription } from "@/modules/patients/services/ch/chMedicalReportSubscription"
import { deMedicalReportSubscription as useDeMedicalReportSubscriptionType } from "@/modules/patients/services/de/__generated__/deMedicalReportSubscription.graphql"
import { deMedicalReportSubscription } from "@/modules/patients/services/de/deMedicalReportSubscription"
import { MedicalReportAttachment } from "@/modules/patients/types/ProgressNoteDocument"
import { RelayEnvironment } from "@/relay-environment"
import { Country } from "@/types/__generated__/union-types"

type MedicalReportSubscriptionType = useChMedicalReportSubscriptionType & useDeMedicalReportSubscriptionType

export const useMedicalReportAttachmentSubscription = (country: Country, patientId: string) => {
  const disposeRef = useRef<() => void>(undefined)

  useEffect(() => {
    const subscription = country === "CH" ? chMedicalReportSubscription : deMedicalReportSubscription
    const config: GraphQLSubscriptionConfig<MedicalReportSubscriptionType> = {
      subscription,
      variables: { patientId },
      updater(store, data) {
        if (!data?.medicalReportAttachmentsChanged) return
        const { attachments, reason } = data.medicalReportAttachmentsChanged
        if (!attachments || !reason) return

        if (reason === "UPLOAD_SESSION_REVERTED") {
          const groupedByProgressNote = groupBy(attachments, (item) => item.progressNoteId)
          const transformed = Object.keys(groupedByProgressNote).map((progressNoteId) => ({
            progressNoteId,
            attachments: groupedByProgressNote[progressNoteId],
          }))

          transformed.forEach(({ attachments: items, progressNoteId }) => {
            const progressNoteRecordProxy = store.get(progressNoteId)
            const medicalReportAttachmentsProxy = progressNoteRecordProxy?.getLinkedRecord("medicalReportAttachments")
            const medicalReportAttachments =
              medicalReportAttachmentsProxy?.getLinkedRecords<MedicalReportAttachment[]>("edges") || []

            const idsToRemove = items.map((a) => a.id)
            const newAttachments = medicalReportAttachments.filter((a) => {
              const id = a.getValue("id")?.toString()
              return id && !idsToRemove.includes(id)
            })
            medicalReportAttachmentsProxy?.setLinkedRecords(newAttachments, "edges")
          })
        }
      },
    }

    const { dispose } = requestSubscription(RelayEnvironment, config)
    disposeRef.current = dispose

    return () => {
      if (disposeRef.current) {
        console.log("Closing medical report attachments subscription")
        disposeRef.current()
        disposeRef.current = undefined
      }
    }
  }, [disposeRef, patientId, country])
}
